/**
 * FaqEditorPanel — dedicated FAQ editor (Phase 11.6.6). Exposes exactly the controls the
 * production FAQ component (`site-builder/components/faq/Faq.tsx`, ported from
 * `__reference__/realDemo/landing/components/faq`) actually renders: a centered section
 * heading + description (same shared `InfoBlock` as every other section — real despite the
 * registry declaring `subheading` unsupported for `faq`), a reorderable list of question/
 * answer items, and, when present, an action button.
 *
 * No Eyebrow — the shared `InfoBlock` never renders eyebrow text, even though `Faq.tsx`
 * passes `content.eyebrow` through (dead prop, same as every other section).
 *
 * No Layout control — the reference FAQ has exactly one design (accordion); the Builder's
 * frozen `cards`/`minimal` layouts have no reference design and silently fall back to the
 * same accordion CSS (see `Faq.tsx`'s own comment). Since the reference editor offers no
 * layout choice, neither does this one.
 *
 * No per-item icon/image — `FaqItem` renders only `item.title` (question) and
 * `item.description` (answer); there is no media slot to expose.
 */
import { Plus, Trash2, ChevronUp, ChevronDown } from 'lucide-react'
import { useBuilderEditor } from './editorContext'
import { Fieldset, TextField, TextArea, ToggleField } from './controls'

export function FaqEditorPanel({ sectionId }: { sectionId: string }) {
  const e = useBuilderEditor()
  const section = e.schema.pages[0]?.sections.find((s) => s.id === sectionId)
  if (!section) {
    return <p className="px-5 py-6 text-xs text-gray-400">This section is no longer available.</p>
  }

  const items = section.content.items ?? []
  const actions = section.content.actions ?? []
  const lastIndex = items.length - 1

  return (
    <div>
      <Fieldset title="Visibility">
        <ToggleField
          label="Show FAQ"
          description="Hide to remove it from the published site"
          checked={section.visible}
          onChange={() => e.toggleSectionVisible(sectionId)}
        />
      </Fieldset>

      <Fieldset title="Content">
        <TextField label="Heading" value={section.content.heading ?? ''} onChange={(v) => e.setSectionText(sectionId, 'heading', v)} />
        <TextArea label="Description" value={section.content.subheading ?? ''} rows={3} onChange={(v) => e.setSectionText(sectionId, 'subheading', v)} />
      </Fieldset>

      <Fieldset title="Questions">
        {items.map((it, i) => (
          <div key={it.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
            <TextField label="Question" value={it.title} onChange={(v) => e.setItemField(sectionId, it.id, 'title', v)} />
            <TextArea label="Answer" value={it.description ?? ''} rows={3} onChange={(v) => e.setItemField(sectionId, it.id, 'description', v)} />
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <button
                  type="button"
                  onClick={() => e.moveItem(sectionId, it.id, 'up')}
                  disabled={i === 0}
                  aria-label="Move question up"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => e.moveItem(sectionId, it.id, 'down')}
                  disabled={i === lastIndex}
                  aria-label="Move question down"
                  className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-30 disabled:hover:bg-transparent"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </div>
              <button
                type="button"
                onClick={() => e.removeItem(sectionId, it.id)}
                className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 transition-colors hover:text-red-600"
              >
                <Trash2 className="h-3.5 w-3.5" /> Remove
              </button>
            </div>
          </div>
        ))}
        <button
          type="button"
          onClick={() => e.addItem(sectionId)}
          className="flex items-center justify-center gap-1 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-xs font-semibold text-gray-600 transition-colors hover:bg-gray-50"
        >
          <Plus className="h-3.5 w-3.5" /> Add question
        </button>
      </Fieldset>

      {actions.length > 0 ? (
        <Fieldset title="Actions">
          {actions.map((a) => (
            <div key={a.id} className="flex flex-col gap-2 rounded-xl border border-gray-100 p-3">
              <TextField label="Label" value={a.label} onChange={(v) => e.setActionField(sectionId, a.id, 'label', v)} />
              <TextField label="Link" value={a.value ?? ''} onChange={(v) => e.setActionField(sectionId, a.id, 'value', v)} />
            </div>
          ))}
        </Fieldset>
      ) : null}
    </div>
  )
}
